import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import pluginZip from "@/assets/plugins/monroya-connect.zip.asset.json";

const MCP_URL = "https://www.monroya.ai/mcp";
const CLAUDE_CONFIG = `{
  "mcpServers": {
    "monroya": {
      "url": "${MCP_URL}"
    }
  }
}`;

function CopyBlock({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="relative mt-3 rounded-md border border-border bg-panel">
      <pre className="overflow-x-auto p-4 pr-20 text-[13px] text-ink">{text}</pre>
      <button
        type="button"
        onClick={() => {
          void navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
          });
        }}
        className="absolute right-2 top-2 rounded-md border border-border bg-paper px-2.5 py-1 text-[13px] text-ink hover:bg-panel"
      >
        {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}
import { MarketingPage, Section } from "@/components/marketing-page";
import { marketingHead, type FAQ } from "@/lib/seo";

const PATH = "/integrations";
const TITLE = "Integrations | monroya.ai";
const DESCRIPTION =
  "Connect WordPress to receive Monroya drafts for review, or access your AI visibility data inside Claude, Cursor and Lovable.";

const FAQS: FAQ[] = [
  {
    q: "How does the WordPress integration work?",
    a: "Install Monroya Connect on your WordPress site, then connect it to your Monroya account. Send drafts with an SEO title, description and supported structured data. You review and publish in WordPress; Monroya never publishes automatically. The integration is included on Starter, Growth and Intelligence, within your plan's monthly draft allowance.",
  },
  {
    q: "Is the Claude, Cursor and Lovable connection safe?",
    a: "Yes. You approve access in your browser with your Monroya account. The connection is read-only, never runs scans, and only shows your own account's data.",
  },
  {
    q: "Do you have a public API?",
    a: "Not yet. CSV export and outbound webhooks are on the near-term roadmap, and a read-side REST API is targeted for Q4 2026. If you need any of these sooner, email us and we'll prioritize against demand.",
  },
  {
    q: "Will my Slack workspace see scan failures?",
    a: "Only the channels you opt in. By default we post scan-complete summaries and high-priority opportunities to one channel of your choice. Failures route to the same channel with a [scan failed] prefix.",
  },
];

type Status = "live" | "beta" | "roadmap";

const INTEGRATIONS: { name: string; status: Status; what: string; }[] = [
  { name: "WordPress", status: "live", what: "Send drafts to your WordPress site with an SEO title, description and supported structured data. You review and publish. Included on paid plans." },
  { name: "Slack alerts", status: "live",    what: "Weekly briefing + high-priority opportunity pings to the channel of your choice." },
  { name: "CSV export",   status: "roadmap", what: "Export the opportunity queue, source ledger, or any scan as CSV for Sheets or ad-hoc analysis. Targeted next." },
  { name: "Webhooks",     status: "roadmap", what: "Outbound webhook on scan complete and new opportunity. JSON payload, HMAC signature, configurable per environment. Targeted next." },
  { name: "Email digest", status: "live",    what: "Weekly executive briefing to any address. Share with your CEO without giving them app access." },
  { name: "Zapier",       status: "beta",    what: "Trigger Zaps from new opportunities. In private beta, email to join." },
  { name: "HubSpot",      status: "roadmap", what: "Push high-intent prompts as HubSpot intent signals. Targeted for Q3 2026." },
  { name: "REST API",     status: "roadmap", what: "Read-side API for opportunities, scans, and the source ledger. Targeted for Q4 2026." },
  { name: "Notion / Linear", status: "roadmap", what: "Push opportunities directly as Linear issues or Notion database rows." },
];

export const Route = createFileRoute("/integrations")({
  head: () =>
    marketingHead({
      title: TITLE,
      description: DESCRIPTION,
      path: PATH,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Integrations", path: PATH },
      ],
      faqs: FAQS,
    }),
  component: IntegrationsPage,
});

const STATUS_STYLE: Record<Status, { label: string; bg: string; color: string }> = {
  live:     { label: "Live",     bg: "rgba(20,184,166,0.12)",  color: "#0E8074" },
  beta:     { label: "Beta",     bg: "rgba(79,110,247,0.12)",  color: "#3B4FE0" },
  roadmap:  { label: "Roadmap",  bg: "rgba(107,114,128,0.15)", color: "#374151" },
};

function IntegrationsPage() {
  return (
    <MarketingPage
      stamp="Integrations"
      title="Monroya integrations: WordPress, Claude, Cursor and Lovable"
      dek="Send drafts to WordPress for review and bring your AI visibility data into the tools your team already opens."
      updated="October 2026"
      faqs={FAQS}
      related={[
        { label: "How scans actually run", to: "/methodology" },
        { label: "Pricing", to: "/pricing" },
      ]}
    >
      <Section heading="Send drafts to WordPress">
        <p>
          Monroya Connect sends drafts straight to your WordPress site with an SEO
          title, description and supported structured data. Every post arrives as
          an unpublished draft. You review, edit if you want, and publish in WordPress.
        </p>
        <p>
          Included on Starter, Growth and Intelligence, within your monthly draft allowance.
          Monroya only creates or updates its own unpublished drafts, not your other posts.
        </p>
        <ol className="mt-4 list-decimal space-y-2 pl-6">
          <li>
            <a href={pluginZip.url} download="monroya-connect.zip" className="font-medium text-ink underline underline-offset-4">
              Download Monroya Connect
            </a>
            {" "}and install and activate it in WordPress under Plugins.
          </li>
          <li>In WordPress, open Settings → Monroya and click Connect to Monroya. Approve the connection in Monroya.</li>
          <li>Open a job with a saved draft in Monroya and choose Send to WordPress.</li>
        </ol>
      </Section>
      <Section heading="Use Monroya inside Claude, Cursor and Lovable">
        <p>
          Connect Monroya to your AI assistant and ask it for your visibility
          numbers, your top jobs and the companies you track. You sign in
          with your Monroya account once. Access is read-only: it can't change
          anything or run scans, and you only see your own account.
        </p>
        <div className="mt-5 space-y-4">
          <div className="rounded-md border border-border bg-paper p-5">
            <div className="text-lg font-medium text-ink">Lovable</div>
            <p className="mt-1 text-ink/80">
              Open Connectors, find Monroya AI Insights, click Connect and approve.
            </p>
          </div>
          <div className="rounded-md border border-border bg-paper p-5">
            <div className="text-lg font-medium text-ink">Claude Desktop</div>
            <p className="mt-1 text-ink/80">
              Add this to <code>claude_desktop_config.json</code> (Settings, Developer, Edit config), then restart Claude.
            </p>
            <CopyBlock text={CLAUDE_CONFIG} />
          </div>
          <div className="rounded-md border border-border bg-paper p-5">
            <div className="text-lg font-medium text-ink">Cursor</div>
            <p className="mt-1 text-ink/80">
              Settings, MCP, Add new MCP server. Paste this address, then approve in the browser window that opens.
            </p>
            <CopyBlock text={MCP_URL} />
          </div>
        </div>
      </Section>

      <Section heading="Available now and on the roadmap">
        <div className="space-y-3">
          {INTEGRATIONS.map((i) => (
            <div
              key={i.name}
              className="flex flex-col gap-2 rounded-lg border border-border bg-paper p-5 md:flex-row md:items-start md:gap-5"
            >
              <div className="md:w-[180px] md:shrink-0">
                <div className="flex items-center gap-2">
                  <span
                    className="rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                    style={{ background: STATUS_STYLE[i.status].bg, color: STATUS_STYLE[i.status].color }}
                  >
                    {STATUS_STYLE[i.status].label}
                  </span>
                </div>
                <div className="mt-2 font-serif text-lg text-ink">{i.name}</div>
              </div>
              <p className="text-ink/80">{i.what}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section heading="Want something not on this list?">
        <p>
          We prioritize integrations by customer demand, not by partner
          deals. If you'd sign up today if [X] integration existed,
          tell us:{" "}
          <a
            href="mailto:support@monroya.ai"
            className="font-medium text-ink underline decoration-border underline-offset-4 hover:decoration-ink"
          >
            support@monroya.ai
          </a>
          .
        </p>
      </Section>
    </MarketingPage>
  );
}
